import { Link } from "react-router-dom";
import { Star, Heart, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { money, resolveImg } from "../lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?crop=entropy&cs=srgb&fm=jpg&q=85";

export const ProductCard = ({ product, eager }) => {
  const { addItem, wishlist, toggleWishlist } = useCart();
  const price = product.sale_price || product.price;
  const saved = wishlist.includes(product.slug);
  const hasVariations = product.variations && product.variations.skus?.length > 1;

  return (
    <div data-testid={`product-card-${product.slug}`} className="group bg-white border border-black/10 flex flex-col transition-transform duration-150 ease-out hover:-translate-y-0.5">
      <Link to={`/product/${product.slug}`} className="relative block overflow-hidden aspect-square bg-[#F4F3F0]">
        <img src={resolveImg(product.images?.[0]) || FALLBACK} alt={product.name} loading={eager ? "eager" : "lazy"}
          onError={(e) => { e.currentTarget.src = FALLBACK; }}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {(product.badges || []).slice(0, 2).map((b) => (
            <span key={b} className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${b === "BEST SELLER" ? "bg-[#121212] text-white" : b === "NEW" ? "bg-[#4A5D23] text-white" : "bg-white border border-black/20 text-black"}`}>{b}</span>
          ))}
        </div>
        <button data-testid={`wishlist-btn-${product.slug}`} aria-label="Wishlist"
          onClick={(e) => { e.preventDefault(); toggleWishlist(product.slug); }}
          className={`absolute top-2 right-2 p-2 bg-white/90 border border-black/10 transition-colors duration-150 ${saved ? "text-[#8B0000]" : "text-black/50 hover:text-black"}`}>
          <Heart size={15} fill={saved ? "currentColor" : "none"} />
        </button>
      </Link>
      <div className="p-4 flex flex-col flex-1">
        {product.review_count > 0 ? (
          <div className="flex items-center gap-1 text-[#B8860B] mb-1">
            <Star size={12} fill="currentColor" />
            <span className="text-xs font-semibold text-black">{product.rating}</span>
            <span className="text-xs text-black/40">({product.review_count})</span>
          </div>
        ) : (
          <div className="text-[10px] font-bold uppercase tracking-widest text-black/30 mb-1">{product.brand || "Shodan Select"}</div>
        )}
        <Link to={`/product/${product.slug}`} className="font-heading font-bold text-sm uppercase tracking-tight leading-snug hover:text-[#4A5D23] transition-colors duration-150">{product.name}</Link>
        <p className="text-xs text-black/50 mt-1 line-clamp-2 flex-1">{product.tagline}</p>
        {hasVariations && (
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#4A5D23] mt-2">{product.variations.groups.join(" / ")} options available</p>
        )}
        <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
          <div className="flex items-baseline gap-2 min-w-0">
            <span data-testid={`price-${product.slug}`} className="font-bold text-base">{hasVariations ? `From ${money(price)}` : money(price)}</span>
            {product.sale_price && <span className="text-xs text-black/40 line-through">{money(product.price)}</span>}
          </div>
          {hasVariations ? (
            <Link data-testid={`quick-add-${product.slug}`} to={`/product/${product.slug}`}
              className="flex items-center gap-1 bg-[#121212] text-white px-3 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">
              Options
            </Link>
          ) : (
            <button data-testid={`quick-add-${product.slug}`}
              onClick={() => addItem({ item_type: "product", slug: product.slug, name: product.name, price, image: product.images?.[0] })}
              className="flex items-center gap-1 bg-[#121212] text-white px-3 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">
              <Plus size={12} /> Add
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
